import React, { useEffect, useState } from "react";
import { FileText, TrendingUp, Users, Car, Download, RefreshCw } from "lucide-react";
import { api } from "../api/client";

export default function ReportsView() {
  const [tab, setTab] = useState("revenue"); // revenue | occupancy | drivers
  const [days, setDays] = useState(30);
  const [revenue, setRevenue] = useState([]);
  const [occupancy, setOccupancy] = useState(null);
  const [topDrivers, setTopDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [rev, occ, td] = await Promise.all([
        api.get(`/reports/revenue?days=${days}`),
        api.get(`/reports/occupancy`),
        api.get(`/reports/top-drivers?days=${days}&limit=10`),
      ]);
      setRevenue(rev.data);
      setOccupancy(occ.data);
      setTopDrivers(td.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days]);

  const downloadCSV = () => {
    const token = localStorage.getItem("access_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/rides.csv?days=${days}`;
    fetch(url, { credentials: "include" })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `carreras_${days}dias.csv`;
        a.click();
      });
  };

  const totalRevenue = revenue.reduce((s, d) => s + (d.revenue || 0), 0);
  const totalRides = revenue.reduce((s, d) => s + (d.rides || 0), 0);
  const avgFare = totalRides > 0 ? totalRevenue / totalRides : 0;
  const maxRev = Math.max(...revenue.map(d => d.revenue || 0), 1);

  return (
    <div className="space-y-4 fade-in" data-testid="reports-view">
      <div className="flex justify-between items-center">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#3949AB]">Analítica</div>
          <h1 className="font-display font-bold text-2xl text-neutral-900 mt-0.5">Reportes</h1>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="office-input px-3 py-2 text-sm border border-neutral-200 rounded"
            data-testid="days-selector"
          >
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
          <button
            onClick={load}
            className="p-2 rounded-lg hover:bg-neutral-100 transition"
            data-testid="reports-refresh"
          >
            <RefreshCw className={`w-4 h-4 text-neutral-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={downloadCSV}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-[#3949AB] text-white rounded hover:bg-[#2C3993] transition"
            data-testid="download-csv"
          >
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-neutral-200">
        {[
          { id: "revenue", label: "Ingresos", icon: TrendingUp },
          { id: "occupancy", label: "Ocupación", icon: Car },
          { id: "drivers", label: "Top Conductores", icon: Users },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition ${
              tab === t.id ? 'border-[#3949AB] text-[#3949AB]' : 'border-transparent text-neutral-500 hover:text-neutral-700'
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* REVENUE TAB */}
      {tab === "revenue" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <KPI label="Ingresos Totales" value={`S/ ${totalRevenue.toFixed(2)}`} color="text-emerald-600" />
            <KPI label="Carreras Completadas" value={totalRides} color="text-[#3949AB]" />
            <KPI label="Tarifa Promedio" value={`S/ ${avgFare.toFixed(2)}`} color="text-amber-600" />
          </div>
          <div className="office-card p-6">
            <h3 className="font-semibold text-neutral-800 mb-4">Ingresos por día</h3>
            <div className="space-y-2">
              {revenue.map(d => {
                const pct = (d.revenue / maxRev) * 100;
                const dt = new Date(d.date + 'T00:00:00');
                return (
                  <div key={d.date} className="grid grid-cols-12 gap-2 items-center text-sm">
                    <div className="col-span-2 text-neutral-500">{dt.toLocaleDateString('es-PE', {day:'2-digit',month:'short'})}</div>
                    <div className="col-span-8">
                      <div className="h-6 bg-neutral-100 rounded overflow-hidden relative">
                        <div className="h-full bg-gradient-to-r from-[#3949AB] to-[#5C6BC0] transition-all" style={{ width: `${Math.max(pct, 2)}%` }} />
                        <span className="absolute inset-0 flex items-center px-2 text-xs text-white font-semibold">
                          {d.rides > 0 ? `${d.rides} carreras` : ''}
                        </span>
                      </div>
                    </div>
                    <div className="col-span-2 text-right font-semibold text-neutral-800">S/ {d.revenue.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* OCCUPANCY TAB */}
      {tab === "occupancy" && occupancy && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <KPI label="Conductores Totales" value={occupancy.drivers.total} color="text-neutral-800" />
            <KPI label="En Línea" value={occupancy.drivers.online} color="text-emerald-600" />
            <KPI label="Ocupados" value={occupancy.drivers.busy} color="text-amber-600" />
            <KPI label="Utilización" value={`${occupancy.utilization_pct}%`} color="text-[#3949AB]" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="office-card p-6">
              <h3 className="font-semibold text-neutral-800 mb-4">Estado de flota</h3>
              <StatusBar label="Disponibles" value={occupancy.drivers.available} total={occupancy.drivers.total} color="bg-emerald-500" />
              <StatusBar label="Ocupados" value={occupancy.drivers.busy} total={occupancy.drivers.total} color="bg-amber-500" />
              <StatusBar label="Desconectados" value={occupancy.drivers.offline} total={occupancy.drivers.total} color="bg-neutral-400" />
            </div>
            <div className="office-card p-6">
              <h3 className="font-semibold text-neutral-800 mb-4">Carreras en curso</h3>
              <div className="text-4xl font-bold text-[#3949AB]">{occupancy.rides.active}</div>
              <div className="text-sm text-neutral-500 mt-1">Asignadas o en progreso</div>
              <div className="mt-6 pt-6 border-t border-neutral-100">
                <div className="text-2xl font-bold text-amber-600">{occupancy.rides.pending}</div>
                <div className="text-sm text-neutral-500">Esperando conductor</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TOP DRIVERS TAB */}
      {tab === "drivers" && (
        <div className="office-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50">
                <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">#</th>
                <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">Conductor</th>
                <th className="p-3 text-right text-xs font-semibold uppercase tracking-wider text-neutral-500">Carreras</th>
                <th className="p-3 text-right text-xs font-semibold uppercase tracking-wider text-neutral-500">Ingresos</th>
                <th className="p-3 text-right text-xs font-semibold uppercase tracking-wider text-neutral-500">Prom. tarifa</th>
              </tr>
            </thead>
            <tbody>
              {topDrivers.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-neutral-500">Sin datos en el rango seleccionado</td></tr>
              ) : topDrivers.map((d, i) => (
                <tr key={d.driver_id} className="border-b border-neutral-100 hover:bg-neutral-50 transition">
                  <td className="p-3 text-sm font-semibold text-neutral-600">#{i + 1}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-neutral-400' : i === 2 ? 'bg-amber-700' : 'bg-[#3949AB]'}`}>
                        {d.driver_name?.substring(0,1) || '?'}
                      </div>
                      <span className="font-medium text-neutral-800">{d.driver_name}</span>
                    </div>
                  </td>
                  <td className="p-3 text-right text-neutral-700">{d.rides}</td>
                  <td className="p-3 text-right font-semibold text-emerald-600">S/ {d.revenue.toFixed(2)}</td>
                  <td className="p-3 text-right text-neutral-600">S/ {d.avg_fare.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, color }) {
  return (
    <div className="office-card p-4">
      <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function StatusBar({ label, value, total, color }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-neutral-600">{label}</span>
        <span className="font-semibold text-neutral-800">{value} / {total}</span>
      </div>
      <div className="h-2 bg-neutral-100 rounded overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
